import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/*
 * 08 October 2026 - SKU 'CONTAINS' MATCHING
 *
 * Shopify's search only matches the START of a word or SKU, so a fragment from the
 * middle of a SKU (e.g. msh240 inside RAPIDMSH240V) finds nothing, and there is no
 * setting to change that. To let the sales team type any part of a SKU, this file
 * keeps its own list of every SKU in the catalogue and matches the typed text
 * against it:
 *
 *  1. When the search dialog opens, the list is downloaded from the mr-sku-index
 *     section (sections/mr-sku-index.liquid), 250 products per request, and kept in
 *     localStorage for 24 hours (key: mr_sku_index_v1).
 *  2. As someone types (3+ characters), the text is compared with every SKU,
 *     ignoring case, spaces and punctuation. Matching on the middle of a SKU needs
 *     4+ characters, to avoid noise from very short fragments.
 *  3. Matching products (best first: exact, starts with, contains) are rendered by
 *     the mr-sku-matches section (sections/mr-sku-matches.liquid) and shown in a
 *     'SKU matches' block above Shopify's own results. Anything already in that
 *     block is removed from Shopify's list so nothing appears twice.
 *
 * It fails safe: if the list cannot be loaded, or anything here throws, the
 * dropdown shows Shopify's normal results exactly as it did before. Only products
 * published to the Online Store are in the list, and new SKUs appear within 24 hours.
 *
 * This covers the dropdown only. The full results page (after pressing Enter) still
 * uses Shopify's own matching, plus the exact / starts-with SKU ordering in
 * sections/search-results.liquid.
 */
const SKU_INDEX_SECTION_ID = 'mr-sku-index';
const SKU_MATCHES_SECTION_ID = 'mr-sku-matches';
const SKU_INDEX_STORAGE_KEY = 'mr_sku_index_v1';
const SKU_INDEX_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const SKU_INDEX_PARALLEL_REQUESTS = 4;
const SKU_MIN_TERM_LENGTH = 3;
const SKU_MIN_CONTAINS_LENGTH = 4;
const SKU_MAX_MATCHES = 6;

/**
 * @typedef {object} SkuRow
 * @property {string} normalised - The SKU in lower case with only letters and numbers
 * @property {number} productId - The id of the product the SKU belongs to
 */

/** @type {SkuRow[] | null} */
let skuRows = null;

/** @type {Promise<SkuRow[]> | null} */
let skuRowsPromise = null;

/**
 * Lower-cases a SKU or search term and strips everything except letters and numbers.
 * @param {unknown} value - The SKU or term
 * @returns {string} The normalised text
 */
function normaliseSku(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Reads the stored SKU list, if there is one and it is still fresh.
 * @returns {Array<[string, number]> | null} The stored rows
 */
function readStoredSkuIndex() {
  try {
    const raw = window.localStorage.getItem(SKU_INDEX_STORAGE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw);
    if (!stored || !Array.isArray(stored.rows) || typeof stored.savedAt !== 'number') return null;
    if (Date.now() - stored.savedAt > SKU_INDEX_MAX_AGE_MS) return null;

    return stored.rows;
  } catch (error) {
    return null;
  }
}

/**
 * Stores the SKU list in the browser. Ignored if storage is full or blocked.
 * @param {Array<[string, number]>} rows - The rows to store
 */
function storeSkuIndex(rows) {
  try {
    window.localStorage.setItem(SKU_INDEX_STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), rows }));
  } catch (error) {
    // Storage is full or blocked - the list is simply downloaded again next time.
  }
}

/**
 * Downloads one page of the SKU list.
 * @param {number} page - The page number, starting at 1
 * @returns {Promise<{ rows: Array<[string, number]>, pages: number }>} The rows and the total number of pages
 */
async function fetchSkuIndexPage(page) {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
  const url = new URL(root, window.location.origin);

  url.searchParams.set('section_id', SKU_INDEX_SECTION_ID);
  url.searchParams.set('page', String(page));

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`SKU list page ${page} failed (${response.status})`);

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const dataElement = doc.querySelector('script[data-mr-sku-index]');
  if (!dataElement || !dataElement.textContent) throw new Error(`SKU list page ${page} had no data`);

  const rows = JSON.parse(dataElement.textContent);
  if (!Array.isArray(rows)) throw new Error(`SKU list page ${page} was not a list`);

  return { rows, pages: Number(dataElement.getAttribute('data-pages')) || 1 };
}

/**
 * Loads the full SKU list - from the browser's storage if fresh, otherwise by downloading it.
 * @returns {Promise<SkuRow[]>} The searchable rows
 */
async function loadSkuRows() {
  let rows = readStoredSkuIndex();

  if (!rows) {
    const first = await fetchSkuIndexPage(1);
    const downloaded = first.rows.slice();
    const pending = [];
    let failed = false;

    for (let page = 2; page <= first.pages; page++) {
      pending.push(page);
    }

    const worker = async () => {
      while (pending.length > 0) {
        const page = pending.shift();
        if (page === undefined) return;

        try {
          const result = await fetchSkuIndexPage(page);
          downloaded.push(...result.rows);
        } catch (error) {
          failed = true;
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(SKU_INDEX_PARALLEL_REQUESTS, pending.length) }, worker));

    // Only keep a complete list - a partial one would be stored for 24 hours.
    if (!failed) storeSkuIndex(downloaded);

    rows = downloaded;
  }

  return rows.map(([sku, productId]) => ({ normalised: normaliseSku(sku), productId: Number(productId) }));
}

/**
 * Gets the searchable SKU rows, starting the download if it has not started yet.
 * Never rejects: if anything fails this resolves to an empty list and tries again next time.
 * @returns {Promise<SkuRow[]>} The searchable rows
 */
function getSkuRows() {
  if (skuRowsPromise) return skuRowsPromise;

  skuRowsPromise = loadSkuRows()
    .then((rows) => {
      skuRows = rows;
      return rows;
    })
    .catch(() => {
      skuRowsPromise = null;
      return /** @type {SkuRow[]} */ ([]);
    });

  return skuRowsPromise;
}

/**
 * Finds the products whose SKU matches what was typed.
 * @param {SkuRow[]} rows - The searchable rows
 * @param {string} term - What was typed
 * @returns {number[]} Product ids, best match first
 */
function findSkuMatchProductIds(rows, term) {
  const query = normaliseSku(term);
  if (query.length < SKU_MIN_TERM_LENGTH) return [];

  /** @type {Map<number, number>} */
  const bestScore = new Map();
  /** @type {number[]} */
  const firstSeen = [];

  for (const row of rows) {
    if (!row.normalised.includes(query)) continue;

    // 0 = exact, 1 = starts with, 2 = contains somewhere in the middle or end
    const rank = row.normalised === query ? 0 : row.normalised.startsWith(query) ? 1 : 2;
    if (rank === 2 && query.length < SKU_MIN_CONTAINS_LENGTH) continue;

    // Within the same rank, a shorter SKU is the closer match.
    const score = rank * 1000 + row.normalised.length;
    const existing = bestScore.get(row.productId);

    if (existing === undefined) {
      bestScore.set(row.productId, score);
      firstSeen.push(row.productId);
    } else if (score < existing) {
      bestScore.set(row.productId, score);
    }
  }

  return firstSeen
    .map((productId, position) => ({ productId, position, score: bestScore.get(productId) ?? Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => a.score - b.score || a.position - b.position)
    .slice(0, SKU_MAX_MATCHES)
    .map((entry) => entry.productId);
}

/**
 * Gets the markup for the 'SKU matches' block for what was typed.
 * Never rejects: resolves to null when there is nothing to show or anything fails.
 * @param {string} term - What was typed
 * @returns {Promise<string | null>} The section markup, or null
 */
async function getSkuMatchesMarkup(term) {
  try {
    const rows = await getSkuRows();
    const productIds = findSkuMatchProductIds(rows, term);
    if (productIds.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return await sectionRenderer.getSectionHTML(SKU_MATCHES_SECTION_ID, false, url);
  } catch (error) {
    return null;
  }
}

/**
 * Puts the 'SKU matches' block into Shopify's own results markup.
 * Falls back to Shopify's markup untouched if anything is not as expected.
 * @param {string} resultsMarkup - The predictive search section markup from Shopify
 * @param {string} skuMarkup - The mr-sku-matches section markup
 * @returns {string} The combined markup
 */
function composeSkuResults(resultsMarkup, skuMarkup) {
  try {
    const parser = new DOMParser();
    const resultsDoc = parser.parseFromString(resultsMarkup, 'text/html');
    const skuDoc = parser.parseFromString(skuMarkup, 'text/html');

    const skuBlock = skuDoc.querySelector('.predictive-search-results__sku-matches');
    const root = resultsDoc.body.firstElementChild;

    if (!skuBlock || !root || !skuBlock.querySelector('.predictive-search-results__card')) return resultsMarkup;

    const productPath = (/** @type {Element} */ link) =>
      new URL(link.getAttribute('href') || '', window.location.origin).pathname;

    const skuPaths = new Set(Array.from(skuBlock.querySelectorAll('a[href]')).map(productPath));
    const importedBlock = resultsDoc.importNode(skuBlock, true);
    const productsBlock = resultsDoc.getElementById('predictive-search-products');

    // Shopify shows 'no results' when it found nothing - not true any more.
    const noResults = resultsDoc.querySelector('.predictive-search-results__no-results');
    if (noResults) noResults.remove();

    if (productsBlock) {
      productsBlock.before(importedBlock);
    } else {
      const inner = resultsDoc.querySelector('.predictive-search-results__inner');
      if (!inner) return resultsMarkup;

      const queries = inner.querySelector('.predictive-search-results__wrapper-queries');
      if (queries) {
        queries.after(importedBlock);
      } else if (inner.firstElementChild) {
        inner.firstElementChild.after(importedBlock);
      } else {
        inner.append(importedBlock);
      }
    }

    // Remove from Shopify's own product list anything already shown in the SKU block.
    if (productsBlock) {
      for (const card of Array.from(productsBlock.querySelectorAll('.predictive-search-results__card'))) {
        const link = card.querySelector('a[href]');
        if (link && skuPaths.has(productPath(link))) card.remove();
      }

      if (!productsBlock.querySelector('.predictive-search-results__card')) productsBlock.remove();
    }

    // The 'single result' shortcut only applies when there is exactly one product in total.
    const singleResult = resultsDoc.querySelector('[data-single-result-url]');
    if (singleResult && resultsDoc.querySelectorAll('.predictive-search-results__card--product').length !== 1) {
      singleResult.remove();
    }

    return root.outerHTML;
  } catch (error) {
    return resultsMarkup;
  }
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }

    // Start loading the SKU list now so it is ready by the time something is typed.
    getSkuRows();
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * Also looks for products whose SKU contains the search term and shows them first
   * (see the SKU 'CONTAINS' MATCHING note at the top of this file).
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);

    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,page,article,collection');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set(
      'resources[options][fields]',
      'title,body,product_type,variants.title,variants.sku,vendor,tag'
    );

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    /**
     * @param {string | null} resultsMarkup - Shopify's own results
     * @param {string | null} skuMarkup - The SKU matches block, if there is one
     */
    const showResults = (resultsMarkup, skuMarkup) => {
      if (!resultsMarkup) return;

      if (abortController.signal.aborted) return;

      morph(predictiveSearchResults, skuMarkup ? composeSkuResults(resultsMarkup, skuMarkup) : resultsMarkup);

      this.#resetScrollPositions();
    };

    /** @param {unknown} error */
    const handleError = (error) => {
      if (abortController.signal.aborted) return;
      throw error;
    };

    const resultsPromise = sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    const skuMatchesPromise = getSkuMatchesMarkup(searchTerm);

    if (skuRows !== null) {
      // The SKU list is already loaded (the usual case after the first search), so wait
      // for both and draw the dropdown once.
      Promise.all([resultsPromise, skuMatchesPromise])
        .then(([resultsMarkup, skuMarkup]) => showResults(resultsMarkup, skuMarkup))
        .catch(handleError);
    } else {
      // The SKU list is still downloading: show Shopify's results straight away and add
      // the SKU matches when they are ready.
      /** @type {string | null} */
      let latestResultsMarkup = null;
      /** @type {string | null} */
      let latestSkuMarkup = null;

      resultsPromise
        .then((resultsMarkup) => {
          latestResultsMarkup = resultsMarkup;
          showResults(latestResultsMarkup, latestSkuMarkup);
        })
        .catch(handleError);

      skuMatchesPromise.then((skuMarkup) => {
        latestSkuMarkup = skuMarkup;
        if (latestResultsMarkup && latestSkuMarkup) showResults(latestResultsMarkup, latestSkuMarkup);
      });
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
